///
/// EarthBrowser
///
/// Created by Matt Giger on September 23, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Per-user control panel: the single place to inspect AND act on one
/// account. Header actions (roles, suspend with reason/duration,
/// unsuspend, revoke all sessions) plus tabs for overview, devices
/// (with per-device revocation), sign-in history, and the user's audit
/// trail. Opened from the accounts table, trial-abuse views, and Ops
/// task links (?section=users&user=<id>).
///

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import {
  useGetRolesQuery,
  useGrantRoleMutation,
  useRevokeRoleMutation,
  useSuspendUserMutation,
  useUnsuspendUserMutation,
} from '@/redux/api/permissionsApi'
import {
  useGetUserAuditQuery,
  useGetUserDetailQuery,
  useRevokeUserDeviceMutation,
  useRevokeUserSessionsMutation,
  type UserDetail,
} from '@/redux/api/userInsightsApi'
import {
  AlertTriangle,
  Ban,
  Fingerprint,
  KeyRound,
  Link2,
  LogOut,
  MonitorSmartphone,
  ScrollText,
  Shield,
} from 'lucide-react'
import { useState } from 'react'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  )
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-500/70 bg-amber-500/10 text-sm">
      <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
      <span>{children}</span>
    </div>
  )
}

// Roles dialog: toggle any defined role on/off for this user
function RolesDialog({ userId, userEmail, userRoles, onClose }: {
  userId: number
  userEmail: string
  userRoles: string[]
  onClose: () => void
}) {
  const { data: rolesData } = useGetRolesQuery()
  const [grantRole, { isLoading: granting }] = useGrantRoleMutation()
  const [revokeRole, { isLoading: revoking }] = useRevokeRoleMutation()
  const busy = granting || revoking

  const toggle = async (roleId: number, roleName: string, hasRole: boolean) => {
    try {
      if (hasRole) {
        await revokeRole({ roleId, userId }).unwrap()
        toast({ title: 'Role revoked', description: `Removed ${roleName} from ${userEmail}` })
      } else {
        await grantRole({ roleId, userId }).unwrap()
        toast({ title: 'Role granted', description: `Granted ${roleName} to ${userEmail}` })
      }
    } catch {
      toast({ title: 'Error', description: `Failed to ${hasRole ? 'revoke' : 'grant'} role`, variant: 'destructive' })
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle>Manage Roles</DialogTitle>
          <DialogDescription>Assign or remove roles for {userEmail}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {rolesData?.roles.map(role => {
            const hasRole = userRoles.includes(role.name)
            return (
              <div key={role.id} className="flex items-center justify-between p-3 border rounded-xl">
                <div className="flex-1">
                  <div className="font-medium text-sm">{role.name}</div>
                  {role.description && (
                    <div className="text-xs text-muted-foreground mt-1">{role.description}</div>
                  )}
                </div>
                <Button
                  variant={hasRole ? 'destructive' : 'default'}
                  size="sm"
                  disabled={busy}
                  onClick={() => toggle(role.id, role.name, hasRole)}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  {hasRole ? 'Remove' : 'Add'}
                </Button>
              </div>
            )
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Suspend dialog: explicit reason (audited + shown to the user record)
// and a duration; suspension revokes all active sessions server-side.
function SuspendDialog({ userId, userEmail, onClose }: {
  userId: number
  userEmail: string
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [duration, setDuration] = useState('7')
  const [suspendUser, { isLoading }] = useSuspendUserMutation()

  const submit = async () => {
    try {
      await suspendUser({
        userId,
        body: {
          reason: reason.trim(),
          duration_days: duration === 'indefinite' ? undefined : parseInt(duration),
        },
      }).unwrap()
      toast({ title: 'User suspended', description: `${userEmail} is suspended and signed out everywhere.` })
      onClose()
    } catch {
      toast({ title: 'Error', description: 'Failed to suspend user', variant: 'destructive' })
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle>Suspend {userEmail}</DialogTitle>
          <DialogDescription>
            All active sessions are revoked immediately. The reason is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Reason for suspension (required)"
            aria-label="Suspension reason"
            className="rounded-xl min-h-20"
          />
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger className="rounded-xl focus:ring-2 focus:ring-blue-500" aria-label="Suspension duration">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="1" className="rounded-lg">1 day</SelectItem>
              <SelectItem value="7" className="rounded-lg">7 days</SelectItem>
              <SelectItem value="30" className="rounded-lg">30 days</SelectItem>
              <SelectItem value="indefinite" className="rounded-lg">Indefinite</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            variant="destructive"
            disabled={isLoading || reason.trim().length === 0}
            onClick={() => void submit()}
            className="rounded-full"
          >
            {isLoading ? 'Suspending…' : 'Suspend'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function OverviewTab({ data }: { data: UserDetail }) {
  const contentCounts = [
    { label: 'Saved views', value: data.content.saved_views },
    { label: 'Placemarks', value: data.content.placemarks },
    { label: 'Shares', value: data.content.shares },
    { label: 'Datasets', value: data.content.datasets },
    { label: 'Alerts', value: data.content.placemark_alerts },
    { label: 'Push subs', value: data.content.push_subscriptions },
  ]

  return (
    <div className="space-y-3">
      <div className="p-4 rounded-xl border border-border bg-card space-y-2">
        <h4 className="text-sm font-semibold">Subscription</h4>
        {data.subscriptions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No subscription history — free tier</p>
        ) : (
          data.subscriptions.map((sub, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap text-sm">
              <span className="font-medium">{sub.product_name}</span>
              <Badge variant={sub.status === 'active' ? 'default' : 'secondary'} className="rounded-full text-xs">
                {sub.status}
              </Badge>
              {sub.current_period_end && (
                <span className="text-xs text-muted-foreground">
                  {sub.cancel_at_period_end ? 'ends' : 'renews'} {formatDate(sub.current_period_end)}
                </span>
              )}
              <span className="text-xs text-muted-foreground ml-auto">since {formatDate(sub.created_at)}</span>
            </div>
          ))
        )}
      </div>

      <div className="p-4 rounded-xl border border-border bg-card space-y-2">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Auth Methods</h4>
          <span className="text-xs text-muted-foreground">
            {data.linkedAccounts.length} linked · {data.passkeys.length} passkey{data.passkeys.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {data.linkedAccounts.map((account, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-border bg-background text-xs">
              {account.provider ?? account.method}
              {account.email && account.email !== data.user.email && (
                <span className="text-muted-foreground">({account.email})</span>
              )}
            </span>
          ))}
          {data.passkeys.map((passkey, i) => (
            <span key={`pk-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-border bg-background text-xs">
              <Fingerprint className="h-3 w-3" />
              {passkey.name || 'passkey'}
              <span className="text-muted-foreground">used {formatDate(passkey.last_used_at)}</span>
            </span>
          ))}
          {data.linkedAccounts.length === 0 && data.passkeys.length === 0 && (
            <span className="text-sm text-muted-foreground">None</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {contentCounts.map(item => (
          <div key={item.label} className="p-2.5 rounded-xl border border-border bg-card text-center">
            <p className="text-base font-semibold leading-tight">{item.value}</p>
            <p className="text-[10px] text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function DevicesTab({ data }: { data: UserDetail }) {
  const [revokeDevice, { isLoading }] = useRevokeUserDeviceMutation()

  const revoke = async (deviceId: number, name: string) => {
    try {
      await revokeDevice({ userId: data.user.id, deviceId }).unwrap()
      toast({ title: 'Device revoked', description: `${name} was revoked and its sessions terminated.` })
    } catch {
      toast({ title: 'Error', description: 'Failed to revoke device', variant: 'destructive' })
    }
  }

  if (data.devices.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">No registered devices</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs">Device</TableHead>
          <TableHead className="text-xs">OS</TableHead>
          <TableHead className="text-xs">Status</TableHead>
          <TableHead className="text-xs">Last used</TableHead>
          <TableHead className="text-xs">IP</TableHead>
          <TableHead className="text-xs" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.devices.map(device => {
          const name = device.device_name || device.device_model || device.device_type
          return (
            <TableRow key={device.id}>
              <TableCell className="text-xs">{name}</TableCell>
              <TableCell className="text-xs">{device.os_name} {device.os_version}</TableCell>
              <TableCell className="text-xs">
                <Badge variant={device.status === 'active' ? 'outline' : 'secondary'} className="rounded-full text-[10px]">
                  {device.status}
                </Badge>
              </TableCell>
              <TableCell className="text-xs">{formatDateTime(device.last_used_at)}</TableCell>
              <TableCell className="text-xs font-mono">{device.last_ip_address ?? '—'}</TableCell>
              <TableCell className="text-right">
                {device.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => void revoke(device.id, name)}
                    className="rounded-full text-xs h-7"
                  >
                    Revoke
                  </Button>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function SigninsTab({ data }: { data: UserDetail }) {
  if (data.signins.length === 0) {
    return (
      <p className="text-sm text-muted-foreground p-4">
        No recorded sign-ins yet (history begins with the sign-in log deployment)
      </p>
    )
  }
  return (
    <div className="space-y-1 p-1">
      {data.signins.map((signin, i) => (
        <div key={i} className="flex items-center gap-2 text-xs py-1">
          <span className="font-medium w-32">{formatDateTime(signin.created_at)}</span>
          <Badge variant="outline" className="rounded-full text-[10px]">
            {signin.provider ?? signin.method}
          </Badge>
          {signin.kind === 'register' && (
            <Badge className="rounded-full text-[10px] bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/70">
              registration
            </Badge>
          )}
          <span className="text-muted-foreground font-mono ml-auto">{signin.ip_address ?? ''}</span>
        </div>
      ))}
    </div>
  )
}

function AuditTab({ userId }: { userId: number }) {
  const { data, isLoading } = useGetUserAuditQuery(userId)

  if (isLoading) {
    return <Skeleton className="h-24 rounded-xl" />
  }
  const entries = data?.entries ?? []
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">No audit entries for this user</p>
  }

  return (
    <div className="space-y-1 p-1">
      {entries.map(entry => (
        <div key={entry.id} className="flex items-start gap-2 text-xs py-1.5 border-b border-border/50 last:border-0">
          <span className="font-medium w-32 shrink-0">{formatDateTime(entry.created_at)}</span>
          <div className="min-w-0 flex-1">
            <span className="font-mono">{entry.action}</span>
            {entry.resource_type && (
              <span className="text-muted-foreground"> · {entry.resource_type}{entry.resource_id ? ` ${entry.resource_id}` : ''}</span>
            )}
            {entry.actor_id !== null && entry.actor_id !== userId && (
              <span className="text-muted-foreground"> · by {entry.actor_email ?? `user #${entry.actor_id}`}</span>
            )}
            {entry.actor_id === userId && entry.user_id !== null && entry.user_id !== userId && (
              <span className="text-muted-foreground"> · on user #{entry.user_id}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export function UserControlPanel({ userId, onClose }: { userId: number; onClose: () => void }) {
  const { data, isLoading } = useGetUserDetailQuery(userId)
  const [rolesOpen, setRolesOpen] = useState(false)
  const [suspendOpen, setSuspendOpen] = useState(false)
  const [unsuspendUser, { isLoading: unsuspending }] = useUnsuspendUserMutation()
  const [revokeSessions, { isLoading: revokingSessions }] = useRevokeUserSessionsMutation()

  const handleUnsuspend = async () => {
    try {
      await unsuspendUser(userId).unwrap()
      toast({ title: 'User unsuspended' })
    } catch {
      toast({ title: 'Error', description: 'Failed to unsuspend user', variant: 'destructive' })
    }
  }

  const handleRevokeSessions = async () => {
    try {
      const result = await revokeSessions(userId).unwrap()
      toast({ title: 'Sessions revoked', description: `${result.revoked} active session(s) terminated.` })
    } catch {
      toast({ title: 'Error', description: 'Failed to revoke sessions', variant: 'destructive' })
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-3xl">
        {isLoading || !data ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-8 w-2/3 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
        ) : (
          <>
            {rolesOpen && (
              <RolesDialog
                userId={userId}
                userEmail={data.user.email}
                userRoles={data.roles}
                onClose={() => setRolesOpen(false)}
              />
            )}
            {suspendOpen && (
              <SuspendDialog userId={userId} userEmail={data.user.email} onClose={() => setSuspendOpen(false)} />
            )}

            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span>{data.user.email}</span>
                <Badge
                  variant={data.user.status === 'active' ? 'default' : data.user.status === 'suspended' ? 'destructive' : 'secondary'}
                  className="rounded-full"
                >
                  {data.user.status}
                </Badge>
                {data.roles.map(role => (
                  <Badge key={role} variant="outline" className="rounded-full text-xs">{role}</Badge>
                ))}
              </DialogTitle>
              <DialogDescription>
                {[data.user.first_name, data.user.last_name].filter(Boolean).join(' ') || data.user.username || `User #${data.user.id}`}
                {' · '}user #{data.user.id} · joined {formatDate(data.user.created_at)}
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRolesOpen(true)}
                className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <Shield className="h-3.5 w-3.5 mr-1.5" />
                Roles
              </Button>
              {data.user.status === 'suspended' ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unsuspending}
                  onClick={() => void handleUnsuspend()}
                  className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                >
                  Unsuspend
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSuspendOpen(true)}
                  className="rounded-full text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <Ban className="h-3.5 w-3.5 mr-1.5" />
                  Suspend
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={revokingSessions || data.activeSessions === 0}
                    className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
                  >
                    <LogOut className="h-3.5 w-3.5 mr-1.5" />
                    Revoke Sessions ({data.activeSessions})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revoke all sessions</AlertDialogTitle>
                    <AlertDialogDescription>
                      Sign {data.user.email} out of every device immediately. They keep their account and can
                      sign in again. Use this if the account looks compromised.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => void handleRevokeSessions()}
                      className="rounded-full bg-red-600 hover:bg-red-700"
                    >
                      Revoke All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {data.user.status === 'suspended' && (
              <Warning>
                Suspended{data.user.suspended_until ? ` until ${formatDate(data.user.suspended_until)}` : ' indefinitely'}
                {data.user.suspension_reason ? ` — ${data.user.suspension_reason}` : ''}
              </Warning>
            )}
            {data.suppression && (
              <Warning>
                Email suppressed ({data.suppression.reason}) since {formatDate(data.suppression.added_at)} — this user
                receives no email. Manage under Email → Deliverability.
              </Warning>
            )}
            {data.user.deletion_requested_at && (
              <Warning>Account deletion requested {formatDate(data.user.deletion_requested_at)}</Warning>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-xl border border-border bg-card">
              <Fact label="Last sign-in" value={formatDateTime(data.user.last_login_at)} />
              <Fact label="Last active" value={formatDate(data.user.last_active_day)} />
              <Fact label="Active days" value={data.user.active_days.toLocaleString()} />
              <Fact label="Email verified" value={data.user.email_verified ? formatDate(data.user.email_verified_at) || 'yes' : 'no'} />
              <Fact label="Active sessions" value={String(data.activeSessions)} />
              <Fact label="Devices" value={String(data.devices.length)} />
            </div>

            <Tabs defaultValue="overview">
              <TabsList className="rounded-full">
                <TabsTrigger value="overview" className="rounded-full">Overview</TabsTrigger>
                <TabsTrigger value="devices" className="rounded-full">
                  <MonitorSmartphone className="h-3.5 w-3.5 mr-1.5" />
                  Devices
                </TabsTrigger>
                <TabsTrigger value="signins" className="rounded-full">
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                  Sign-ins
                </TabsTrigger>
                <TabsTrigger value="audit" className="rounded-full">
                  <ScrollText className="h-3.5 w-3.5 mr-1.5" />
                  Audit
                </TabsTrigger>
              </TabsList>
              <TabsContent value="overview"><OverviewTab data={data} /></TabsContent>
              <TabsContent value="devices"><DevicesTab data={data} /></TabsContent>
              <TabsContent value="signins"><SigninsTab data={data} /></TabsContent>
              <TabsContent value="audit"><AuditTab userId={userId} /></TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
